import { usePOSProviders } from './usePOSProviders';
import { useConnectionState } from './useConnectionState';
import { useOAuthCallback } from './useOAuthCallback';
import { usePOSConnectionManager } from './usePOSConnectionManager';

/**
 * Main hook for POS Connection
 * Thin wrapper that orchestrates focused sub-hooks
 *
 * @param {boolean} businessDetailsCompleted - Whether business details are completed
 * @param {Object} currentShop - Current shop object
 * @param {Function} onShopRefresh - Callback to refresh shop data
 * @returns {Object} Complete connection state and handlers
 */
export const usePOSConnection = (businessDetailsCompleted, currentShop, onShopRefresh) => {
	const { posList, loading: providersLoading, error: providersError } = usePOSProviders(businessDetailsCompleted, currentShop);

	const stock = useConnectionState('stock', currentShop, posList);
	const product = useConnectionState('product', currentShop, posList);

	const { isProcessing: oauthProcessing } = useOAuthCallback();

	const {
		posLoading,
		error: connectionError,
		includeProductData,
		handleConnectPOS,
		handleEditStock,
		handleEditProduct,
		handleCloseEdit,
		setIncludeProductData,
		setError,
		setPosLoading,
	} = usePOSConnectionManager(currentShop, stock, product, onShopRefresh);

	const posConnectionCompleted = !!(stock.source?.pointOfSale);
	const loadingInventorySources = businessDetailsCompleted && (!currentShop || posList.length === 0);
	const error = providersError || connectionError;

	return {
		// Shared state
		posConnectionCompleted,
		posList,
		posLoading: posLoading || oauthProcessing,
		includeProductData,
		error,
		loadingInventorySources,
		currentShop,

		// Stock connection object with handlers
		stock: {
			...stock,
			onPOSChange: stock.setEditingPOS,
			onFormChange: stock.handleFormChange,
			onEdit: handleEditStock,
		},

		// Product connection object with handlers
		product: {
			...product,
			onPOSChange: product.setEditingPOS,
			onFormChange: product.handleFormChange,
			onEdit: handleEditProduct,
		},

		// Shared handlers
		onConnectPOS: handleConnectPOS,
		onIncludeProductDataChange: setIncludeProductData,
		onCloseEdit: handleCloseEdit,
		onShopRefresh,

		// Internal setters (for ConnectionProvider, not exposed to components)
		setPosLoading,
		setError,
		setPollingForStockConnection: stock.setPollingForConnection,
		setPollingForProductConnection: product.setPollingForConnection,
		setStockConnectionWarning: stock.setConnectionWarning,
		setProductConnectionWarning: product.setConnectionWarning,
	};
};
